<template>
  <div v-touch-pan.horizontal="swipeHandler" :style="swipeStyle" class="recruitment-candidate-card">
    <header>
      <QBtn v-if="canIgnore && !recruitment.isBookingExpress()" class="btn-left" icon="far fa-xmark" flat size="20px" @click="ignoreCandidate" />
      <Avatar :picture="candidate.getPicture()" size="115px" @click="goToProfile" />
      <span v-if="candidate.isFavorite()" class="recruitment-candidate-card-status"><QIcon name="favorite" /></span>
    </header>

    <article @click="goToProfile">
      <!-- Identity -->
      <section class="recruitment-candidate-card-identity">
        <span class="recruitment-candidate-card-name">{{ candidate.getFirstname() }}</span>
        <span v-if="candidate.getPostalCode()" class="recruitment-candidate-card-postal-code">({{ candidate.getPostalCode() }})</span>
      </section>
      <!-- Experience -->
      <section v-if="!candidate.isCanceledByExtra()">
        <span class="recruitment-candidate-card-jobs">
          {{ $tc("ETAB.PAGES.CANDIDATE.CARD.@JOBS", candidate.getExperienceAmount()) }}
        </span>
        <template v-if="candidate[ICandidate.note] > 0">
          <span class="recruitment-candidate-card-separator" />
          <StarsNotation :note="candidate.getNote()" :notes-amount="candidate.getNotesAmount()" />
        </template>
        <template v-if="totalReports">
          <span class="recruitment-candidate-card-separator" />
          <span class="recruitment-candidate-card-jobs">
            <QIcon name="fad fa-triangle-exclamation" color="red" size="xs" class="q-mr-xs q-mb-xs" />
            {{ $t("ETAB.PAGES.CANDIDATE.CARD.@REPORTS_SHORT", { count: totalReports }) }}
          </span>
        </template>
      </section>
      <section class="q-mt-md">
        <div class="text-right">
          <QChip v-if="candidate.getTags().isNovice()" color="primary" text-color="white" :label="$t('ETAB.PAGES.CANDIDATE.CARD.@NOVICE')" />
        </div>
      </section>
      <!-- Top Recommendation -->
      <section v-if="showExperience">
        <TopRecommendation :recommendation="candidate.getRecommendation()" />
      </section>
    </article>
    <footer v-if="canContact" class="recruitment-candidate-card-contact">
      <ExtraBtnColumn flat no-caps color="white" icon="icon-f-eye" :label="$t('ETAB.PAGES.CANDIDATE.CARD.@GO_TO_PROFILE')" @click="goToProfile" />
      <ExtraBtnColumn
        v-if="hasCandidateContactInformations"
        flat
        no-caps
        color="white"
        icon="icon-extra-contact"
        :label="$t('ETAB.PAGES.CANDIDATE.CARD.@CONTACT')"
        @click="contactCandidate"
      />
    </footer>
    <footer v-if="candidate.isCanceledByExtra()" @click="goToProfile">
      <Cancellation :comment="candidate.getCanceledComment()" :recruitment="recruitment" />
    </footer>
  </div>
</template>

<script setup>
  import { CandidateModel, ICandidate, RecruitmentDefaultModel } from "@core/models";
  import StarsNotation from "@core/components/common/StarsNotation.component.vue";
  import TopRecommendation from "@etab/components/candidates/TopRecommendation.component.vue";
  import Cancellation from "@etab/components/candidates/Cancellation.component.vue";
  import { reactive, computed, defineEmits } from "vue";

  const SWIPE_DISTANCE = 175;
  const emit = defineEmits(["ignoreCandidate", "goToProfile", "contactCandidate"]);

  const swipeStyle = reactive({});
  const swipeTimer = reactive(undefined);

  const props = defineProps({
    candidate: {
      type: Object,
      default: () => new CandidateModel(),
    },
    recruitment: {
      type: Object,
      default: () => new RecruitmentDefaultModel(),
    },
    canContact: {
      type: Boolean,
      default: false,
    },
    canIgnore: {
      type: Boolean,
      default: false,
    },
    showExperience: {
      type: Boolean,
      default: false,
    },
  });

  const totalReports = computed(() => {
    return props.candidate.getCandidateReports()?.getTotalReports();
  });

  const hasCandidateContactInformations = computed(() => {
    const { phone, email } = props.candidate.getIdentity();
    return phone !== undefined || email !== undefined;
  });

  const swipeHandler = function (swipe) {
    // we can swipe left only
    if (props.canIgnore && swipe.direction === "left") {
      clearTimeout(swipeTimer);
      const distance = swipe.distance.x;
      if (distance > 5) {
        swipeStyle = { transform: `translate(${-distance}px)` };
      }
      // we apply action only at the end of the swipe (when the user put his finger up)
      if (swipe.isFinal) {
        // after an arbitrary distance, we consider the user wanted to act on props.candidate
        if (distance > SWIPE_DISTANCE) {
          ignoreCandidate();
          swipeStyle = { transform: `translate(${-1000}px)` };
        } else {
          // we reset the UI at the end of the action
          swipeTimer = setTimeout(() => {
            swipeStyle = { transform: `translate(0px)` };
          }, 250);
        }
      }
    }
  };

  const ignoreCandidate = function () {
    emit("ignoreCandidate", { candidate: props.candidate });
  };

  const goToProfile = function () {
    emit("goToProfile", { candidate: props.candidate });
  };

  const contactCandidate = function () {
    emit("contactCandidate", { candidate: props.candidate });
  };
</script>
